import React, { useState, useRef, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useSounds } from '../hooks/useSounds';
import { useFavoritesContext } from '../context/FavoritesContext';
import SEOOptimized from './SEOOptimized';
import InternalLinksEngine, { SEOBreadcrumbs, RelatedContent } from './InternalLinksEngine';
import Header from './Header';
import Footer from './Footer';
import Breadcrumb from './Breadcrumb';
import './InternalLinksEngine.css';

import { FaPlay, FaPause, FaDownload, FaHeart, FaRegHeart, FaArrowLeft, FaMusic, FaClock, FaTag } from 'react-icons/fa';
import './SoundPage.css';

import { useNavigate } from 'react-router-dom';
import { getCategoryUrl, categoryToSlug } from '../utils/urlUtils';

const SoundPage = ({ setCurrentPage, darkMode: darkModeProp, toggleDarkMode: toggleDarkModeProp }) => {
  const { soundSlug } = useParams();
  const navigate = useNavigate();
  const { sounds, loading } = useSounds();
  
  // Dark mode fallback
  const [darkModeState, setDarkModeState] = useState(() => {
    if (darkModeProp !== undefined) return darkModeProp;
    const saved = localStorage.getItem('soundnest-theme');
    return saved ? saved === 'dark' : true;
  });
  const darkMode = darkModeProp !== undefined ? darkModeProp : darkModeState;

  const toggleDarkMode = toggleDarkModeProp ? toggleDarkModeProp : () => {
    setDarkModeState(prev => {
      const next = !prev;
      localStorage.setItem('soundnest-theme', next ? 'dark' : 'light');
      document.body.className = next ? 'dark-theme' : 'light-theme';
      return next;
    });
  };
  const { favorites, addToFavorites, removeFromFavorites, isFavorite } = useFavoritesContext();
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // Trouver le son par slug
  const sound = sounds.find(s => createSlug(s.title) === soundSlug);

  // Reset state when navigating between sound pages
  useEffect(() => {
    // Stop any currently playing audio
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
    }
    // Reset the state
    setIsPlaying(false);
    // Scroll to top
    window.scrollTo(0, 0);
  }, [soundSlug]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => setIsPlaying(false);
    audio.addEventListener('ended', handleEnded);

    return () => audio.removeEventListener('ended', handleEnded);
  }, []);

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  if (!sound) {
    return <Navigate to="/" replace />;
  }

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const handleToggleFavorite = () => {
    if (isFavorite(sound.id)) {
      removeFromFavorites(sound.id);
    } else {
      addToFavorites(sound);
    }
  };

  // Define a primary tag (could be subcategory or category)
  const primaryCategory = sound && sound.tags && sound.tags.length > 0 ? sound.tags[0] : 'Général';

  // Infer category/subcategory for clean URLs
  const normalize = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const primaryNorm = normalize(primaryCategory);

  // Heuristic mapping of subcategories to their parent categories
  const subcatGroups = {
    'Effets sonores': ['swoosh','explosion','impact','cartoon','notification','marche','objet','transition','science fiction','magique','metal','bois','glitch','scifi','interface'],
    'Nature': ['montagne','océan','ocean','feu','riviere','desert','pluie','grotte','orage','animaux','oiseaux','feuillage','vent','foret','rivière'],
    'Ambiance': ['ville','cafe','rue','bar','bus','metro','bureau','halloween','espace','nuit','manifestation','foule','aeroport','plage','spa'],
    'Interface': ['interface']
  };

  let crumbCategory = null;
  let crumbSubcategory = null;

  // If the primary tag matches a top-level category exactly
  if (categoryToSlug[primaryCategory]) {
    crumbCategory = primaryCategory;
  } else {
    // Try to find which category this subcategory belongs to
    for (const [catName, list] of Object.entries(subcatGroups)) {
      if (list.includes(primaryNorm)) {
        crumbCategory = catName;
        crumbSubcategory = primaryCategory; // keep original label (with accents)
        break;
      }
    }
  }

  // Build clean breadcrumb paths
  const categoryPath = crumbCategory ? getCategoryUrl(crumbCategory, crumbSubcategory || null) : null;

  // Generate breadcrumbs for UI/SEO with clean URLs
  const breadcrumbs = sound ? (
    [
      ...(categoryPath ? [{ name: crumbSubcategory || crumbCategory, url: categoryPath }] : []),
      { name: sound.title, url: `/sons/${soundSlug}` }
    ]
  ) : [];

  // Generate enhanced title and description for SEO
  const seoTitle = sound ? `${sound.title} - Son ${primaryCategory} Gratuit` : 'Son Introuvable';
  const seoDescription = sound ? 
    `Téléchargez gratuitement "${sound.title}" - un son de qualité professionnelle de la catégorie ${primaryCategory}. Fichier audio libre de droits disponible en MP3 et WAV. Durée: ${sound.duration}. Parfait pour vos projets créatifs, vidéos, podcasts et jeux vidéo.` :
    'Ce son n\'a pas été trouvé sur SoundNest.';

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AudioObject",
    "name": sound.title,
    "description": `Son gratuit "${sound.title}" libre de droits. Catégorie: ${primaryCategory}. Durée: ${sound.duration}.`,
    "contentUrl": sound.downloadUrl,
    "encodingFormat": "audio/mpeg",
    "duration": sound.duration,
    "genre": primaryCategory,
    "license": "https://creativecommons.org/publicdomain/zero/1.0/",
    "creator": {
      "@type": "Organization",
      "name": "LaSonotheque",
      "url": "https://www.lasonotheque.org"
    },
    "publisher": {
      "@type": "Organization",
      "name": "SoundNest",
      "url": "https://soundnest.vercel.app"
    }
  };

  return (
    <div className={`app ${darkMode ? 'dark-theme' : 'light-theme'}`}>
      <SEOOptimized
        type="sound"
        sound={sound}
        currentUrl={`https://sound-nest.vercel.app/sons/${soundSlug}`}
        breadcrumbs={breadcrumbs.map(crumb => ({
          name: crumb.name,
          url: `https://sound-nest.vercel.app${crumb.url}`
        }))}
      />
      
      <Header 
        currentPage="sound"
        setCurrentPage={setCurrentPage ? setCurrentPage : () => navigate('/')}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        favoritesCount={favorites.length}
        selectedCategory={"all"}
        setSelectedCategory={(id)=> {
          // id here is expected to be a category label (e.g., 'Nature')
          const path = getCategoryUrl(id) || '/';
          navigate(path);
        }}
      />

      <main className="sound-page-main">
        <div className="sound-page-container">
          <SEOBreadcrumbs items={breadcrumbs} />
          
          <button 
            className="back-button"
            onClick={() => {
              if (setCurrentPage) {
                setCurrentPage('home');
              } else {
                navigate('/');
              }
            }}
          >
            <FaArrowLeft /> Retour à l'accueil
          </button>

          <div className="sound-page-content">
            <article className="sound-info">
              <header className="sound-header">
                <h1 className="sound-title">{sound.title}</h1>
                
                <div className="sound-meta">
                  <div className="sound-meta-item">
                    <FaTag className="meta-icon" />
                    <span className="sound-theme">Catégorie: {sound.theme}</span>
                  </div>
                  <div className="sound-meta-item">
                    <FaClock className="meta-icon" />
                    <span className="sound-duration">Durée: {sound.duration}</span>
                  </div>
                  <div className="sound-meta-item">
                    <FaMusic className="meta-icon" />
                    <span className="sound-license">Licence: Libre de droits</span>
                  </div>
                </div>
              </header>

              <section className="sound-description">
                <h2 className="sr-only">Description du son</h2>
                <p>
                  Téléchargez gratuitement <strong>"{sound.title}"</strong> - un son de qualité professionnelle 
                  de la catégorie <strong>{sound.theme}</strong>. Ce fichier audio est <strong>libre de droits</strong> et 
                  peut être utilisé gratuitement dans vos projets personnels et commerciaux, vidéos, podcasts, 
                  jeux vidéo et créations multimédias.
                </p>
                <p>
                  Disponible en formats <strong>MP3</strong> et <strong>WAV</strong> haute qualité. 
                  Durée: <strong>{sound.duration}</strong>. Aucune attribution requise.
                </p>
              </section>
            </article>

            <section className="sound-player" aria-label="Lecteur audio et téléchargement">
              <h2 className="sr-only">Écouter et télécharger le son</h2>
              
              <div className="audio-controls">
                <button 
                  className="play-button"
                  onClick={handlePlayPause}
                  aria-label={isPlaying ? `Mettre en pause ${sound.title}` : `Écouter ${sound.title}`}
                >
                  {isPlaying ? <FaPause /> : <FaPlay />}
                  {isPlaying ? 'Pause' : 'Écouter'}
                </button>

                <button 
                  className="favorite-button"
                  onClick={handleToggleFavorite}
                  aria-label={isFavorite(sound.id) ? `Retirer ${sound.title} des favoris` : `Ajouter ${sound.title} aux favoris`}
                >
                  {isFavorite(sound.id) ? <FaHeart /> : <FaRegHeart />}
                  {isFavorite(sound.id) ? 'Retiré des favoris' : 'Ajouter aux favoris'}
                </button>
              </div>

              <audio 
                ref={audioRef}
                src={sound.downloadUrl}
                onEnded={() => setIsPlaying(false)}
              />

              <section className="download-section" aria-label="Téléchargement du fichier audio">
                <h3 className="download-title">Télécharger gratuitement</h3>
                
                <div className="download-buttons">
                  {sound.mp3Url && (
                    <a
                      href={sound.mp3Url}
                      className="download-button mp3"
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Télécharger ${sound.title} en format MP3`}
                    >
                      <FaDownload /> MP3
                    </a>
                  )}
                  {sound.wavUrl && (
                    <a
                      href={sound.wavUrl}
                      className="download-button wav"
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Télécharger ${sound.title} en format WAV`}
                    >
                      <FaDownload /> WAV (Haute qualité)
                    </a>
                  )}
                </div>
                
                <div className="download-info">
                  <p>
                    <strong>Fichier audio gratuit et libre de droits.</strong> Choisissez le format MP3 (plus léger) 
                    ou WAV (qualité maximale) selon vos besoins.
                  </p>
                  <p className="download-source">
                    Source originale: <a href="https://www.lasonotheque.org" target="_blank" rel="noopener noreferrer">LaSonotheque.org</a>
                  </p>
                </div>
              </section>
            </section>
          </div>
          
          {/* SEO Internal Links - Styled */}
          <div className="seo-sections">
            <InternalLinksEngine 
              currentSound={sound}
              currentCategory={sound?.theme}
              sounds={sounds}
            />
            
            {/* Related Content */}
            <RelatedContent 
              currentSound={sound}
              sounds={sounds}
            />
          </div>
        </div>
      </main>

      <Footer currentPage="sound" setCurrentPage={setCurrentPage ? setCurrentPage : () => navigate('/')} />
    </div>
  );
};

// Fonction utilitaire pour créer un slug
function createSlug(title) {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
    .replace(/[^a-z0-9\s-]/g, '') // Supprime les caractères spéciaux
    .replace(/\s+/g, '-') // Remplace les espaces par des tirets
    .replace(/-+/g, '-') // Supprime les tirets multiples
    .trim();
}

export { createSlug };
export default SoundPage;
